import { differenceInMilliseconds, getTime } from "@suse-o11y/date-time";
import { formatViewInterval } from "@suse-o11y/date-time/format/format-view-interval";
import { floor } from "@suse-o11y/floor";
import { theme } from "@suse-o11y/twin-migration";
import clamp from "lodash/clamp";
import range from "lodash/range";
import React from "react";

import { Group, type GroupProps, Line, Text, useContainerSize } from "../_canvas";

import { useTimelineContext, useTimelineRange } from "./_context";

const DEFAULT_TICK_GAP_PX = 250;
const TICK_LINE_HIGH_PX = 6;
const TICK_TEXT_SIZE_PX = 11;
const TICK_COLOR = theme("colors.gray.500");
const TEXT_COLOR = TICK_COLOR;

export interface TimelineTimeTicksProps extends Pick<GroupProps, "x" | "y"> {
	withTicks?: boolean;
	tickGap?: number;
}

export const TimelineTimeTicks: React.FC<TimelineTimeTicksProps> = ({
	withTicks,
	x,
	y,
	tickGap = DEFAULT_TICK_GAP_PX,
}) => {
	const { timestamp, timeRange } = useTimelineContext();
	const { width } = useContainerSize();
	// it either keeps the time on each relative time range has changed
	// or end time of a specific time range in which case timePassed == 0
	const timeRangeChangeTime = React.useMemo(() => timeRange.endDate(), [timeRange]);
	const [timelineStart, timelineEnd] = useTimelineRange();

	const duration = differenceInMilliseconds(timelineEnd, timelineStart);
	const tickStep = clamp(tickGap / width, 0.05, 0.33);
	const timePassed = differenceInMilliseconds(timelineEnd, timeRangeChangeTime);
	const tickStepPassed = (timePassed / duration) % tickStep;

	const time = formatViewInterval({
		interval: [timelineStart, timelineEnd],
		// if timestamp is not defined - it is a live mode, so the end time is the view time
		viewTime: timestamp ?? timelineEnd,
	});

	// ticks might show only part of the tick, since the line is moving
	// so it renders the ticks according the passed time, with a half of tick step shift
	// to avoid ugly look when the component just mounted
	const ticks = range(tickStep / 2 - tickStepPassed, 1 + tickStep, tickStep);
	const tickWidth = floor(width / ticks.length, 0);

	return (
		<Group listening={false} x={x} y={y}>
			{ticks.map((tick) => {
				const left = tick * width;

				return (
					<Group key={tick} x={left}>
						<Text
							align="center"
							fill={TEXT_COLOR}
							fontFamily={theme("fontFamily.text")}
							fontSize={TICK_TEXT_SIZE_PX}
							offsetX={tickWidth / 2}
							width={tickWidth}
						>
							{time.format(floor(getTime(timelineStart) + duration * tick, 0))}
						</Text>

						{withTicks && (
							<Line
								points={[
									{ x: 0, y: 1 + TICK_TEXT_SIZE_PX },
									{ x: 0, y: 1 + TICK_TEXT_SIZE_PX + TICK_LINE_HIGH_PX },
								]}
								stroke={TICK_COLOR}
								strokeWidth={1}
							/>
						)}
					</Group>
				);
			})}
		</Group>
	);
};
